/**
 * This package's IndexedDB layer, in full: connection lifecycle, the schema, and
 * the handful of record operations built on top of it.
 *
 * It began as idb-keyval plus a custom store factory, because idb-keyval has no
 * notion of an index: its `createStore` opens without a version, so the upgrade
 * it runs can only ever create a bare object store. Once the factory was ours,
 * what remained of the library was a set of one-line wrappers over the store
 * function below — and keeping it split was actively hazardous. Whichever
 * connection opens a database first is the one that creates its object store, so
 * a caller reaching for idb-keyval's `createStore` against *our* database would
 * silently create the store without the index and quietly demote cleanup to a
 * full scan, with no error to notice. Owning the whole layer removes both the
 * dependency and the footgun.
 */

/** The contract every operation below is written against: run `callback` with the object store, inside a transaction. */
export type IndexedDbStore = <T>(
  txMode: IDBTransactionMode,
  callback: (store: IDBObjectStore) => T | PromiseLike<T>,
) => Promise<T>

export function promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

/**
 * Writes resolve on the *transaction*, not the request, so a resolved promise
 * means the data is durable rather than merely accepted.
 */
function promisifyTransaction(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onabort = transaction.onerror = () => reject(transaction.error)
  })
}

/**
 * Index over the absolute expiry timestamp written by `setItem`. Entries that
 * never expire have no value at the key path, and a record whose key path
 * resolves to `undefined` is simply not added to the index — which is exactly
 * the semantics we want, since those records must never be cleaned up.
 */
export const EXPIRES_AT_INDEX = 'expiresAt'
const EXPIRES_AT_KEY_PATH = 'state.expiresAt'

/**
 * The database a given store lives in. Each object store gets a database of its
 * own, so that asking one base name for two different stores cannot collide.
 * Exported so callers that key other state off "which database is this" -- the
 * cleanup throttle, the cross-tab lock -- name the same thing this opens.
 */
export const databaseNameFor = (baseName: string, storeName: string) => `${baseName}::${storeName}`

/**
 * Opens (creating if needed) a database whose object store carries an index over
 * each record's expiry timestamp, so cleanup can range-query the expired keys
 * instead of deserializing every cached payload to ask "is this expired?".
 *
 * Opens *without* a version, so it can only ever run the upgrade that creates
 * the database — the index is created with the store and never migrated into an
 * existing one. That restriction is deliberate: a versioned open of a database
 * another tab still holds is blocked, and a blocked request sits at the head of
 * that database's connection queue, stalling every later open — including any
 * fallback. On the hydration path that would mean no cache at all until the user
 * closed their other tabs. Existing databases are left alone and a new schema
 * gets a new database name (see `DBNAME_PREFIX`).
 *
 * Because only that first upgrade ever runs, a database can never gain a second
 * object store: a later open for a different `storeName` would find the database
 * already there, skip `onupgradeneeded`, and then throw `NotFoundError` on every
 * transaction. So the store name selects the *database* rather than living
 * inside a shared one, which makes that state unreachable instead of merely
 * undocumented.
 */
export function createExpiryIndexedStore(baseName: string, storeName: string): IndexedDbStore {
  const dbName = databaseNameFor(baseName, storeName)
  let dbPromise: Promise<IDBDatabase> | undefined

  // Opening lazily, and dropping the cached promise when the connection closes,
  // means the connection is only paid for by callers that use the store, and
  // Safari closing a connection on its own doesn't strand every later read on a
  // dead promise.
  function getDb(): Promise<IDBDatabase> {
    if (dbPromise) return dbPromise

    const request = globalThis.indexedDB.open(dbName)
    request.onupgradeneeded = () =>
      request.result.createObjectStore(storeName).createIndex(EXPIRES_AT_INDEX, EXPIRES_AT_KEY_PATH)

    const pending = promisifyRequest(request)
    dbPromise = pending
    void (async () => {
      try {
        const db = await pending
        const release = () => {
          if (dbPromise === pending) dbPromise = undefined
        }
        db.onclose = release
        // Fired when another connection -- in practice a tab running a newer
        // bundle -- wants to delete or upgrade this database. Holding the
        // connection open would leave that request `blocked` and parked at the
        // head of this database's connection queue, stalling every later open of
        // it. Closing immediately is what lets the *next* schema generation
        // reclaim this one without ever blocking IndexedDB.
        //
        // `close()` does not fire `onclose`, so the cached promise is released
        // here too. A later read reopens, which can recreate a database that was
        // just deleted; an empty database is a cheap price for never blocking,
        // and the next generation's cleanup pass collects it.
        db.onversionchange = () => {
          db.close()
          release()
        }
      } catch {
        // Callers see the failure through the promise they await. A rejected
        // open stays cached, and is recovered from by the persister recreating
        // the store outright.
      }
    })()

    return dbPromise
  }

  return async (txMode, callback) => {
    const db = await getDb()
    return callback(db.transaction(storeName, txMode).objectStore(storeName))
  }
}

export function get<T>(key: IDBValidKey, withStore: IndexedDbStore): Promise<T | undefined> {
  return withStore('readonly', store => promisifyRequest<T | undefined>(store.get(key)))
}

export function set(key: IDBValidKey, value: unknown, withStore: IndexedDbStore): Promise<void> {
  return withStore('readwrite', store => {
    store.put(value, key)
    return promisifyTransaction(store.transaction)
  })
}

export function del(key: IDBValidKey, withStore: IndexedDbStore): Promise<void> {
  return withStore('readwrite', store => {
    store.delete(key)
    return promisifyTransaction(store.transaction)
  })
}

/** One transaction for the whole batch, so the deletes commit or fail together. */
export function delMany(keysToDelete: IDBValidKey[], withStore: IndexedDbStore): Promise<void> {
  return withStore('readwrite', store => {
    for (const key of keysToDelete) store.delete(key)
    return promisifyTransaction(store.transaction)
  })
}

export function setMany(entriesToSet: Array<[IDBValidKey, unknown]>, withStore: IndexedDbStore): Promise<void> {
  return withStore('readwrite', store => {
    for (const [key, value] of entriesToSet) store.put(value, key)
    return promisifyTransaction(store.transaction)
  })
}

export function clear(withStore: IndexedDbStore): Promise<void> {
  return withStore('readwrite', store => {
    store.clear()
    return promisifyTransaction(store.transaction)
  })
}

export function keys(withStore: IndexedDbStore): Promise<IDBValidKey[]> {
  return withStore('readonly', store => promisifyRequest(store.getAllKeys()))
}

/**
 * Deserializes every record in the store, so it is expensive by nature — cleanup
 * deliberately uses the expiry index instead. Both requests are issued before
 * the first `await`, since a transaction closes once its task yields without
 * pending work.
 */
export function entries<KeyType extends IDBValidKey, ValueType>(
  withStore: IndexedDbStore,
): Promise<Array<[KeyType, ValueType]>> {
  return withStore('readonly', async store => {
    const keyRequest = store.getAllKeys()
    const valueRequest = store.getAll()

    const [allKeys, allValues] = await Promise.all([promisifyRequest(keyRequest), promisifyRequest(valueRequest)])

    return allKeys.map((key, index): [KeyType, ValueType] => [key as KeyType, allValues[index] as ValueType])
  })
}
